/// <reference lib="dom" />
/**
 * Frontend ID-token cache + refresher.
 *
 * Canonical shared definition lifted to `@ams/shared/auth` in materials-03b
 * Phase 1. All consumers (host, BOM, inventory, future modules) import the
 * single shared singleton so cross-module Cloud Function calls reuse one
 * cached id-token instead of minting four copies.
 *
 * Refresh contract:
 *   - 401 → cookie expired, throws `CookieExpiredError`.
 *   - 403 with `csrf_invalid`/`csrf_missing` body → invalidates the
 *     CsrfManager and throws `CsrfInvalidError`. Caller is expected to
 *     re-login (which repopulates the CSRF token).
 *   - Any other non-2xx → generic refresh error.
 */
import { CsrfInvalidError } from './csrf-manager.js';
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
export class CookieExpiredError extends Error {
    cookieExpired = true;
    constructor() {
        super('Session cookie expired — please log in again.');
        this.name = 'CookieExpiredError';
    }
}
export function createIdTokenManager(deps) {
    const fetchFn = deps.fetch ?? globalThis.fetch.bind(globalThis);
    const now = deps.now ?? (() => new Date());
    let cache = null;
    async function refresh() {
        const res = await fetchFn(`${deps.apiBase}/me/id-token`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'X-CSRF-Token': deps.csrfManager?.get() ?? '' },
        });
        if (res.status === 401) {
            cache = null;
            throw new CookieExpiredError();
        }
        if (res.status === 403) {
            const errBody = (await res.json().catch(() => ({})));
            if (errBody.error === 'csrf_invalid' || errBody.error === 'csrf_missing') {
                cache = null;
                deps.csrfManager?.invalidate();
                throw new CsrfInvalidError();
            }
            throw new Error(`id-token refresh failed: 403 ${errBody.error ?? ''}`.trim());
        }
        if (!res.ok) {
            throw new Error(`id-token refresh failed: ${res.status}`);
        }
        const body = (await res.json());
        cache = { idToken: body.idToken, expiresAt: new Date(body.expiresAt) };
        return cache.idToken;
    }
    return {
        async getIdToken() {
            const nowMs = now().getTime();
            if (cache == null)
                return refresh();
            if (cache.expiresAt.getTime() - nowMs < REFRESH_BUFFER_MS) {
                return refresh();
            }
            return cache.idToken;
        },
        invalidate() {
            cache = null;
        },
    };
}
//# sourceMappingURL=id-token-manager.js.map